import { ApplicationInsights } from '@microsoft/applicationinsights-web';

let appInsights: ApplicationInsights | null = null;
let isInitialized = false;

/**
 * Initializes Application Insights with connection string from environment variable.
 * If connection string is not provided, AppInsights logging will be disabled.
 */
export const initializeApplicationInsights = (): boolean => {
  if (isInitialized) {
    return appInsights !== null;
  }

  const connectionString = import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING;

  if (!connectionString) {
    isInitialized = true;
    return false;
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxErrorStatusText: true,
        enableAjaxPerfTracking: true,
        maxAjaxCallsPerView: -1,
        disableExceptionTracking: false,
        disableTelemetry: false,
        loggingLevelConsole: 1,
        loggingLevelTelemetry: 1,
      },
    });

    appInsights.loadAppInsights();
    appInsights.trackPageView();

    isInitialized = true;
    return true;
  } catch (error) {
    console.error('[AppInsights] Failed to initialize:', error);
    isInitialized = true;
    return false;
  }
};

/**
 * Tracks a custom event in Application Insights.
 */
export const trackEvent = (eventName: string, properties?: Record<string, string>): void => {
  if (!appInsights) return;

  try {
    appInsights.trackEvent({ name: eventName }, properties);
  } catch (error) {
    console.error('[AppInsights] Failed to track event:', error);
  }
};

/**
 * Tracks a trace message in Application Insights.
 */
export const trackTrace = (
  message: string,
  severityLevel: 'Verbose' | 'Information' | 'Warning' | 'Error' | 'Critical' = 'Information',
  properties?: Record<string, string>
): void => {
  if (!appInsights) return;

  try {
    const severityMap: Record<string, 0 | 1 | 2 | 3 | 4> = {
      Verbose: 0,
      Information: 1,
      Warning: 2,
      Error: 3,
      Critical: 4,
    };

    appInsights.trackTrace(
      {
        message: message,
        severityLevel: severityMap[severityLevel],
      },
      properties
    );
  } catch (error) {
    console.error('[AppInsights] Failed to track trace:', error);
  }
};

/**
 * Tracks an exception in Application Insights.
 */
export const trackException = (
  error: Error | string,
  properties?: Record<string, string>
): void => {
  if (!appInsights) return;

  try {
    appInsights.trackException(
      {
        exception: error instanceof Error ? error : new Error(error),
      },
      properties
    );
  } catch (trackError) {
    console.error('[AppInsights] Failed to track exception:', trackError);
  }
};
